import React from "react";
import { Alert, Box, Button, Chip, IconButton, Link, Paper, TextField, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { PageContainer, SectionHeader, SelectorBar, SelectField, FormDialog, DataTable } from "../../Layout/Common";
import { gs, Loader, StaleHint, ensureOptions, canEdit, StateChip, STATES, IdText, Cut, EventsTable, FIXED, ELLIPSIS, MONO,
         ago, when, fromUnix, n, regionLabel, regionOptions, versionLabel, majorMinor, shortId, openMachine } from "./servers";

// Game Servers → Sessions: the title's matches / rooms / worlds (`servers sessions`, newest first, finished ones kept
// 7 days) with filters (live = allocating + ready + active by default, or one state; profile, player, server), one session's detail (`servers session <id>`: seats, server,
// endpoint, the title's data and the game's result, its events) with End session (`session-end`), and the live worlds
// (`servers worlds`) with Add / Remove (`world-add`, `world-remove`). Polled every 5 s (the first page of the list).
// Deep link: /<game>/servers/sessions/<sessionId>.

const PAGE = 50;
const ANY = "*";
const ID_RE = /^[0-9a-f]{24}$/i;
const LIVE_FILTER = "live";      // the state select's `live: true` (allocating, ready and active)
const DEFAULT_FILTERS = { state: LIVE_FILTER, profile: ANY, playerId: "", serverId: "" };
const PLAYER = { reserved: "info", joined: "success", left: "default", expired: "default" };
const LIVE = new Set(["allocating", "ready", "active"]);

const json = (v) => (v == null ? null : typeof v === "string" ? v : JSON.stringify(v, null, 2));
const seats = (s) => `${n(s.seated)} / ${n(s.joined)} / ${n(s.maxPlayers)}`;
const endpoint = (s) => (s.ip ? `${s.ip}${(s.ports ?? []).length ? ":" + s.ports.join(",") : ""}` : null);
const sessionVersion = (s) => versionLabel(s.branch, s.versionText || `${s.major}.${s.minor}`);

function JsonBox({ label, value }) {
    const text = json(value);
    return <Box sx={{ flex: 1, minWidth: 280 }}>
        <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
        {text == null
            ? <Typography variant="body2" color="text.secondary">—</Typography>
            : <Box component="pre" sx={{ ...MONO, m: 0, p: 1, bgcolor: "action.hover", borderRadius: 1, maxHeight: 260, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{text}</Box>}
    </Box>;
}

export default class SessionsPage extends React.Component {
    state = {
        options: null, optionsError: null, loadedAt: null, stale: null,
        sessions: null, more: false, skip: 0, filters: { ...DEFAULT_FILTERS }, nonce: 0, idError: null,
        worlds: null,
        selected: globals.route?.deep?.[0] || null, session: null, sessionError: null, events: null,
        end: null,               // { session, reason } while the End dialog is open
        world: null,             // the Add world dialog's fields
        busy: false,
    };

    loader = new Loader(this, async () => {
        ensureOptions(this);
        const { skip, sessions, filters: f } = this.state;
        const selected = ID_RE.test(this.state.selected ?? "") ? this.state.selected : null;   // a malformed link id is refused by the backend
        const payload = { skip, limit: PAGE };
        if (f.state === LIVE_FILTER) payload.live = true;
        else if (f.state !== ANY) payload.state = f.state;
        if (f.profile !== ANY) payload.profile = f.profile;
        if (f.playerId) payload.playerId = f.playerId;
        if (f.serverId) payload.serverId = f.serverId;
        const keepPage = skip > 0 && sessions && !this.force;   // an older page stays still unless asked
        this.force = false;
        const [list, w, one, ev] = await Promise.all([
            keepPage ? null : gs("Loading sessions", ["sessions"], { payload, silent: true }),
            gs("Loading worlds", ["worlds"], { silent: true }),
            selected ? gs("Loading the session", ["session", selected], { silent: true }).catch(e => ({ error: e?.message || "failed" })) : null,
            selected ? gs("Loading the session's events", ["events"], { payload: { sessionId: selected, limit: 100 }, silent: true }) : null,
        ]);
        return {
            ...(list ? { sessions: list.sessions ?? [], more: !!list.more } : {}),
            worlds: w?.worlds ?? [],
            ...(selected ? { session: one?.session ?? null, sessionError: one?.error ?? null, events: ev?.events ?? [] } : {}),
        };
    });

    componentDidMount() { this.loader.start(); }
    componentWillUnmount() { this.loader.stop(); }

    // ----- list -----
    apply = (patch) => this.setState(st => ({ filters: { ...st.filters, ...patch }, skip: 0, sessions: null, idError: null }), this.loader.reload);
    setId = (key, value) => {
        const v = value.trim();
        if (v === this.state.filters[key]) return;
        if (v && !ID_RE.test(v)) { this.setState({ idError: `${key === "playerId" ? "Player" : "Server"} id must be 24 hex characters.` }); return; }
        this.apply({ [key]: v });
    };
    filterPlayer = (playerId) => this.setState(st => ({ nonce: st.nonce + 1 }), () => this.apply({ playerId }));
    page = (skip) => this.setState({ skip: Math.max(0, skip), sessions: null }, this.loader.reload);
    clear = () => this.setState(st => ({ nonce: st.nonce + 1 }), () => this.apply({ ...DEFAULT_FILTERS }));

    // ----- detail -----
    select = (id) => {
        this.setState({ selected: id, session: null, sessionError: null, events: null }, this.loader.reload);
        replaceDeep(id ? [id] : []);
    };

    // ----- actions -----
    endSession = () => {
        const { session, reason } = this.state.end;
        this.setState({ busy: true });
        gs("Ending the session", ["session-end", session.id], { payload: reason.trim() ? { reason: reason.trim() } : {}, retry: false })
            .then(r => {
                if (r?.session) this.setState(st => ({
                    session: st.selected === r.session.id ? r.session : st.session,
                    sessions: st.sessions && st.sessions.map(x => (x.id === r.session.id ? r.session : x)),
                }));
                this.setState({ end: null });
                this.loader.load();
            })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    };
    removeWorld = (w) => {
        const s = w.world;
        if (!window.confirm(`Remove world '${s.name ?? shortId(s.id)}'?\n\nThe world ends now (its ${n(s.joined)} joined players are disconnected) and its server stops.`)) return;
        gs("Removing the world", ["world-remove", s.id], { retry: false })
            .then(r => { if (r?.worlds) this.setState({ worlds: r.worlds }); this.loader.load(); })
            .catch(() => {});
    };

    // ----- add world -----
    persistentProfiles = () => (this.state.options?.profiles ?? []).filter(p => String(p.mode).toLowerCase() === "persistent");
    // The major.minors of a branch that have a server build (newest first)
    lines = (branch) => {
        const seen = new Set(), out = [];
        for (const b of (this.state.options?.builds ?? []).filter(x => x.branch === branch).sort((a, b) => b.version - a.version)) {
            const mm = majorMinor(b.versionText);
            if (mm && !seen.has(mm)) { seen.add(mm); out.push({ value: mm, label: `${mm} (newest patch ${b.versionText})` }); }
        }
        return out;
    };
    openWorld = () => {
        const branches = this.state.options?.branches ?? [];
        const branch = branches.includes("Development") ? "Development" : (branches[0] ?? "");
        this.setState({ world: { name: "", profile: this.persistentProfiles()[0]?.id ?? "", branch, line: this.lines(branch)[0]?.value ?? "", region: "", maxPlayers: "", data: "" } });
    };
    setWorld = (patch) => this.setState(st => {
        const world = { ...st.world, ...patch };
        if ("branch" in patch && !this.lines(world.branch).some(l => l.value === world.line)) world.line = this.lines(world.branch)[0]?.value ?? "";
        return { world };
    });
    worldError = () => {
        const w = this.state.world;
        if (!w.name.trim()) return "A world needs a name.";
        if (!w.profile) return "Pick a persistent profile.";
        if (!w.branch || !/^\d+\.\d+$/.test(w.line)) return "Pick a branch and a major.minor with a server build.";
        if (String(w.maxPlayers).trim() !== "" && !(parseInt(w.maxPlayers, 10) > 0)) return "Max players must be > 0 (empty = the profile's).";
        if (w.data.trim()) { try { JSON.parse(w.data); } catch (e) { return `Data is not valid JSON: ${e.message}`; } }
        return "";
    };
    addWorld = () => {
        const w = this.state.world;
        const [major, minor] = w.line.split(".").map(x => parseInt(x, 10));
        const payload = { name: w.name.trim(), profile: w.profile, branch: w.branch, major, minor };
        if (w.region) payload.region = w.region;
        if (String(w.maxPlayers).trim() !== "") payload.maxPlayers = parseInt(w.maxPlayers, 10);
        if (w.data.trim()) payload.data = JSON.parse(w.data);
        this.setState({ busy: true });
        gs("Adding the world", ["world-add"], { payload, retry: false })
            .then(r => { if (r?.worlds) this.setState({ worlds: r.worlds }); this.setState({ world: null }); })
            .catch(() => {})
            .finally(() => this.setState({ busy: false }));
    };

    // ----- render -----
    renderDetail() {
        const { selected, session: s, sessionError, events } = this.state;
        if (!selected) return null;
        if (!ID_RE.test(selected)) return <Alert severity="error" sx={{ mb: 3 }} onClose={() => this.select(null)}>"{selected}" is not a session id (24 hex characters).</Alert>;
        const edit = canEdit();
        const field = (label, value) => <Box sx={{ minWidth: 0 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Box sx={{ fontSize: 14, ...ELLIPSIS }}>{value ?? "—"}</Box></Box>;
        const playerColumns = [
            { id: "id", label: "Player (click: their sessions)", width: 150, render: p => <IdText id={p.id} onClick={() => this.filterPlayer(p.id)} /> },
            { id: "state", label: "Seat", width: 90, render: p => <Chip size="small" color={PLAYER[p.state] ?? "default"} variant={(PLAYER[p.state] ?? "default") === "default" ? "outlined" : "filled"} label={p.state} /> },
            { id: "reservedAt", label: "Reserved", width: 150, render: p => when(p.reservedAt) },
            { id: "reserveUntil", label: "Reserved until", width: 150, render: p => when(p.reserveUntil) },
            { id: "joinedAt", label: "Joined", width: 150, render: p => when(p.joinedAt) },
            { id: "leftAt", label: "Left", width: 150, render: p => when(p.leftAt) },
        ];
        return <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                <Typography variant="h6">Session</Typography>
                <Box sx={{ ...MONO, fontSize: 14 }}>{selected}</Box>
                {s && <StateChip kind="session" state={s.state} title={`since ${when(s.stateChangedAt)}`} />}
                {s?.persistent && <Chip size="small" variant="outlined" label={`world${s.name ? " · " + s.name : ""}`} />}
                <Box sx={{ flex: 1 }} />
                {edit && s && LIVE.has(s.state) && <Button size="small" color="warning" variant="outlined" startIcon={<StopCircleOutlinedIcon />}
                                                           onClick={() => this.setState({ end: { session: s, reason: "" } })}>End session</Button>}
                <Tooltip title="Close"><IconButton size="small" onClick={() => this.select(null)}><CloseIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
            {sessionError && <Alert severity="warning" sx={{ mb: 1 }}>{sessionError}</Alert>}
            {!s ? (!sessionError && <Typography variant="body2" color="text.secondary">Loading…</Typography>) : <>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 1.5, mb: 2 }}>
                    {field("Profile", s.profile)}
                    {field(s.persistent ? "Name" : "Tag", <Cut text={s.persistent ? s.name : s.tag} />)}
                    {field("Version", sessionVersion(s))}
                    {field("Region", regionLabel(s.region, this.state.options))}
                    {field("Server", s.serverId ? <IdText id={s.serverId} onClick={s.machineId ? () => openMachine(s.machineId, s.serverId) : null} /> : "—")}
                    {field("Endpoint", endpoint(s) && <Box component="span" sx={MONO}>{endpoint(s)}</Box>)}
                    {field("Seats seated / joined / max", seats(s))}
                    {field("Created", when(s.createdDate))}
                    {field("Ready", when(s.readyAt))}
                    {field("Active (first join)", when(s.activeAt))}
                    {field("Ended", when(s.endedAt))}
                    {s.reason && field("Reason", <Cut text={s.reason} />)}
                </Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Players ({(s.players ?? []).length})</Typography>
                {(s.players ?? []).length === 0
                    ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No seat taken.</Typography>
                    : <DataTable columns={playerColumns} rows={s.players} getRowKey={p => p.id} sx={{ ...FIXED, mb: 2 }} />}
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                    <JsonBox label="Data (the title's, given at creation)" value={s.data} />
                    <JsonBox label="Result (the game's, from sessions end)" value={s.result} />
                </Box>
            </>}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Events</Typography>
            {events == null ? <Typography variant="body2" color="text.secondary">Loading…</Typography> : <EventsTable events={events} compact />}
        </Paper>;
    }

    renderWorlds() {
        const { worlds, options } = this.state;
        const edit = canEdit();
        const columns = [
            { id: "name", label: "World", width: 140, render: w => <Tooltip title={`open session ${w.world.id}`}>
                <Link component="button" underline="hover" onClick={() => this.select(w.world.id)} sx={{ ...ELLIPSIS, display: "block", maxWidth: "100%", fontWeight: 600, textAlign: "left" }}>{w.world.name ?? shortId(w.world.id)}</Link>
              </Tooltip> },
            { id: "profile", label: "Profile", width: 90, sx: ELLIPSIS, render: w => w.world.profile },
            { id: "version", label: "Version", width: 95, render: w => sessionVersion(w.world) },
            { id: "region", label: "Region", width: 80, sx: ELLIPSIS, render: w => <span title={regionLabel(w.world.region, options)}>{w.world.region ?? "—"}</span> },
            { id: "state", label: "State", width: 95, render: w => <StateChip kind="session" state={w.world.state} title={w.world.reason ?? `since ${when(w.world.stateChangedAt)}`} /> },
            { id: "players", label: "Seated / joined / max", width: 100, align: "right", render: w => seats(w.world) },
            { id: "server", label: "Server", width: 185, render: w => w.server
                ? <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                    <StateChip kind="server" state={w.server.state} title={`${versionLabel(w.server.branch, w.server.versionText)} · desired ${w.server.desired}`} />
                    <IdText id={w.server.id} onClick={() => openMachine(w.server.machineId, w.server.id)} />
                  </Box>
                : <Typography variant="body2" color="text.secondary">none placed yet</Typography> },
            { id: "successor", label: "Successor", width: 105, render: w => <IdText id={w.successor} /> },
            { id: "notice", label: "Stop scheduled", width: 110, render: w => w.server?.noticeAt > 0
                ? <Tooltip title={when(fromUnix(w.server.noticeAt))}><span>{fromUnix(w.server.noticeAt).toLocaleString([], { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span></Tooltip> : "—" },
            ...(edit ? [{ id: "actions", label: "", width: 50, render: w => <Tooltip title="Remove the world (it ends and its server stops)">
                <IconButton size="small" color="error" onClick={() => this.removeWorld(w)}><DeleteIcon fontSize="small" /></IconButton></Tooltip> }] : []),
        ];
        return <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                <Typography variant="h6">Worlds</Typography>
                {edit && <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={this.openWorld}>Add world</Button>}
            </Box>
            {worlds == null
                ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
                : worlds.length === 0
                    ? <Typography variant="body2" color="text.secondary">No live world.</Typography>
                    : <DataTable columns={columns} rows={worlds} getRowKey={w => w.world.id} sx={FIXED} />}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                A world is a persistent session with its own server; it never rolls to a new patch by itself. Maintenance: schedule a stop on its
                server (open the server: Machines page) — at that time a successor on the newest patch is placed and the world re-opens there.
            </Typography>
        </Box>;
    }

    renderList() {
        const { sessions, more, skip, filters, nonce, idError, options } = this.state;
        const edit = canEdit();
        const columns = [
            { id: "state", label: "State", width: 95, render: s => <StateChip kind="session" state={s.state} title={`since ${when(s.stateChangedAt)}`} /> },
            { id: "id", label: "Session", width: 110, render: s => <IdText id={s.id} onClick={() => this.select(s.id)} /> },
            { id: "profile", label: "Profile", width: 90, sx: ELLIPSIS, render: s => s.profile },
            { id: "tag", label: "Tag / name", width: 115, render: s => <Cut text={s.persistent ? s.name : s.tag} /> },
            { id: "version", label: "Version", width: 90, render: s => sessionVersion(s) },
            { id: "region", label: "Region", width: 70, sx: ELLIPSIS, render: s => s.region ?? "—" },
            { id: "seats", label: "Seated / joined / max", width: 100, align: "right", render: s => seats(s) },
            { id: "server", label: "Server", width: 105, render: s => <IdText id={s.serverId} onClick={s.machineId ? () => openMachine(s.machineId, s.serverId) : null} /> },
            { id: "created", label: "Created", width: 90, render: s => <Tooltip title={when(s.createdDate)}><span>{ago(s.createdDate)}</span></Tooltip> },
            { id: "end", label: "Ended · reason", width: 185, render: s => s.endedAt || s.reason
                ? <Cut text={`${s.endedAt ? ago(s.endedAt) : ""}${s.endedAt && s.reason ? " · " : ""}${s.reason ?? ""}`} /> : "—" },
            ...(edit ? [{ id: "actions", label: "", width: 50, render: s => LIVE.has(s.state)
                ? <Tooltip title="End session"><IconButton size="small" color="warning" onClick={() => this.setState({ end: { session: s, reason: "" } })}><StopCircleOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                : null }] : []),
        ];
        const active = filters.state !== DEFAULT_FILTERS.state || filters.profile !== ANY || filters.playerId || filters.serverId;
        return <>
            <Typography variant="h6" sx={{ mb: 1 }}>Sessions</Typography>
            <SelectorBar sx={{ flexWrap: "wrap" }}>
                <SelectField label="State" size="small" minWidth={150} value={filters.state} onChange={v => this.apply({ state: v })}
                             options={[{ value: LIVE_FILTER, label: "Live" }, { value: ANY, label: "Any state" }, ...STATES.session]} />
                <SelectField label="Profile" size="small" minWidth={150} value={filters.profile} onChange={v => this.apply({ profile: v })}
                             options={[{ value: ANY, label: "Any profile" }, ...(options?.profiles ?? []).map(p => p.id)]} />
                {[["playerId", "Player id"], ["serverId", "Server id"]].map(([key, label]) => (
                    <TextField key={`${key}-${nonce}`} size="small" label={label} sx={{ width: 240 }} defaultValue={filters[key]}
                               slotProps={{ htmlInput: { style: { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 } } }}
                               onKeyDown={e => { if (e.key === "Enter") this.setId(key, e.target.value); }}
                               onBlur={e => this.setId(key, e.target.value)} />
                ))}
                <Button size="small" onClick={this.clear} disabled={!active}>Clear</Button>
            </SelectorBar>
            {idError && <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>{idError}</Typography>}
            {sessions == null
                ? <Typography variant="body2" color="text.secondary">Loading…</Typography>
                : sessions.length === 0
                    ? <Typography variant="body2" color="text.secondary">{active ? "No session matches these filters." : "No live session."}</Typography>
                    : <DataTable columns={columns} rows={sessions} getRowKey={s => s.id} sx={FIXED} />}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                <Button size="small" disabled={skip === 0} onClick={() => this.page(skip - PAGE)}>← Newer</Button>
                <Typography variant="caption" color="text.secondary">
                    {sessions && sessions.length > 0 ? `sessions ${skip + 1}–${skip + sessions.length}, newest first` : ""}{skip > 0 ? " · not refreshed on older pages" : ""}
                </Typography>
                <Button size="small" disabled={!more} onClick={() => this.page(skip + PAGE)}>Older →</Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Finished sessions (ended, failed, lost) are kept 7 days. Seats: reserved or joined / joined / the session's maximum. A pooled
                session nobody joined within 2 minutes is ended by the backend; a session whose players all left is ended by its server.
            </Typography>
        </>;
    }

    renderEnd() {
        const { end, busy } = this.state;
        if (!end) return null;
        const s = end.session;
        return <FormDialog open title={`End session ${shortId(s.id)}?`} onClose={() => this.setState({ end: null })} onConfirm={this.endSession}
                           confirmText="End session" confirmDisabled={busy} maxWidth="sm" fullWidth>
            <Typography variant="body2" sx={{ mb: 2 }}>
                {s.persistent ? `World '${s.name ?? ""}'` : `${s.profile}${s.tag ? ` (${s.tag})` : ""}`}, {s.state}, {n(s.joined)} player(s) joined. It
                ends now: the slot is freed, its server gets <code>session-close</code> and disconnects the players, and the title's
                OnSessionEnded runs with no result.{s.persistent ? " A world's server keeps running: use Remove world to stop it as well." : ""}
            </Typography>
            <TextField fullWidth label="Reason (optional)" placeholder="ended by you" value={end.reason} onChange={e => this.setState({ end: { ...end, reason: e.target.value } })} />
        </FormDialog>;
    }

    renderWorldDialog() {
        const { world: w, options, optionsError, busy } = this.state;
        if (!w) return null;
        const err = this.worldError();
        const profiles = this.persistentProfiles();
        const lines = this.lines(w.branch);
        const p = profiles.find(x => x.id === w.profile);
        return <FormDialog open title="Add a world" onClose={() => this.setState({ world: null })} onConfirm={this.addWorld}
                           confirmText="Add world" confirmDisabled={!!err || busy} maxWidth="sm" fullWidth>
            {!options && <Alert severity={optionsError ? "error" : "info"} sx={{ mb: 1 }}>{optionsError ? `Could not load the profiles and builds: ${optionsError}` : "Loading the profiles and builds…"}</Alert>}
            {options && profiles.length === 0 && <Alert severity="warning" sx={{ mb: 1 }}>This title declares no persistent profile (GameServersConfig.profiles).</Alert>}
            <TextField fullWidth label="Name (unique among the live worlds)" sx={{ mt: 1 }} value={w.name} onChange={e => this.setWorld({ name: e.target.value })} />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <SelectField label="Profile (persistent)" fullWidth value={w.profile} onChange={v => this.setWorld({ profile: v })}
                             options={profiles.map(x => ({ value: x.id, label: `${x.id} · ${x.maxPlayers} players · ${x.weight} unit(s)` }))} />
                <SelectField label="Branch" fullWidth value={w.branch} onChange={v => this.setWorld({ branch: v })} options={options?.branches ?? []} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <SelectField label="Version (major.minor)" fullWidth value={w.line} onChange={v => this.setWorld({ line: v })} options={lines} disabled={lines.length === 0} />
                <SelectField label="Region" fullWidth value={w.region || ANY} onChange={v => this.setWorld({ region: v === ANY ? "" : v })}
                             options={[{ value: ANY, label: `Default (${options?.defaultRegion ?? "the title's"})` }, ...regionOptions(options, ["local"])]} />
            </Box>
            {lines.length === 0 && options && <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>No server build on {w.branch}: upload one on the Database page.</Typography>}
            <TextField label="Max players" type="number" sx={{ mt: 2, width: 220 }} value={w.maxPlayers} onChange={e => this.setWorld({ maxPlayers: e.target.value })}
                       helperText={p ? `empty = the profile's ${p.maxPlayers}` : "empty = the profile's"} slotProps={{ htmlInput: { min: 1 } }} />
            <TextField label="Data (the title's JSON, optional)" fullWidth multiline minRows={3} maxRows={10} sx={{ mt: 2 }} value={w.data}
                       onChange={e => this.setWorld({ data: e.target.value })} slotProps={{ htmlInput: { style: { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 } } }} />
            {err && <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>{err}</Typography>}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                The world's server is placed at once on a machine with room (newest patch of that major.minor) and the world opens when
                the server is ready; the server reads the data with <code>sessions get</code>. Players reach it through the title's own code.
            </Typography>
        </FormDialog>;
    }

    render() {
        const { loadedAt, stale, sessions, worlds } = this.state;
        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="Sessions">
                    <StaleHint loadedAt={loadedAt} stale={stale} />
                    <Tooltip title="Refresh"><IconButton size="small" onClick={() => { this.force = true; this.loader.reload(); }}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                </SectionHeader>
                {stale && sessions == null && worlds == null && <Alert severity="warning" sx={{ mb: 2 }}>Could not load the sessions: {stale}</Alert>}
                {this.renderDetail()}
                {this.renderWorlds()}
                {this.renderList()}
                {this.renderEnd()}
                {this.renderWorldDialog()}
            </PageContainer>
        );
    }
}
